import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { procedureAPI, patientAPI, doctorAPI, csrfHeaders } from '../../lib/api';
import { formatDateTime, getStatusColor, formatCurrency, apiErrorMessage } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Stethoscope, Plus, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';
import SearchableSelect from '../../components/SearchableSelect/SearchableSelect';
import SuggestItemModal from '../../components/SuggestItem/SuggestItemModal';
import { logger } from '../../lib/logger';

const PROCEDURE_TYPES = ['Minor Surgery', 'Major Surgery', 'Diagnostic', 'Therapeutic', 'Emergency', 'Cosmetic', 'Other'];

const ProcedureList = () => {
  const { user, hasRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [procedures, setProcedures] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientHistory, setPatientHistory] = useState([]);
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  
  // Get patient_id from URL
  const queryParams = new URLSearchParams(location.search);
  const patientIdFromUrl = queryParams.get('patient_id');
  
  const [formData, setFormData] = useState({
    patient_id: patientIdFromUrl || '',
    procedure_name: '',
    procedure_type: '',
    doctor_id: '',
    scheduled_date: '',
    notes: '',
    price: 0,
  });

  useEffect(() => {
    if (patientIdFromUrl) {
      fetchPatientHistory(patientIdFromUrl);
      setDialogOpen(true);
    }
  }, [patientIdFromUrl]);

  const fetchData = useCallback(async () => {
    try {
      const params = patientIdFromUrl ? { patient_id: patientIdFromUrl } : {};
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const [procRes, patRes, docRes] = await Promise.all([
        procedureAPI.getAll(params),
        patientAPI.getAll({}),
        doctorAPI.getAll(),
      ]);
      setProcedures(procRes.data.procedures || procRes.data || []);
      setPatients(patRes.data.patients || patRes.data || []);
      setDoctors(docRes.data.doctors || docRes.data || []);
      
      // Set selected patient if from URL
      if (patientIdFromUrl) {
        const patientsArray = patRes.data.patients || patRes.data || [];
        const patient = patientsArray.find(p => p.id === patientIdFromUrl);
        setSelectedPatient(patient);
      }
    } catch (error) {
      logger.error('Error loading procedure data:', error);
      toast.error(apiErrorMessage(error, 'Failed to load procedure data. Please try again.'));
      setProcedures([]);
      setPatients([]);
      setDoctors([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, patientIdFromUrl]);


  useEffect(() => {
    fetchData();
  }, [statusFilter, fetchData]);
  const fetchPatientHistory = async (patientId) => {
    try {
      const response = await procedureAPI.getAll({ patient_id: patientId });
      setPatientHistory(response.data.procedures || response.data || []);
    } catch (error) {
      logger.error('Failed to load patient history');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        doctor_id: hasRole('doctor') ? user.id : formData.doctor_id,
      };
      
      // Auto-create suggestion for new procedure if needed (non-blocking)
      if (formData.procedure_name && formData.procedure_name.trim()) {
        try {
          const searchResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/procedures/search?q=${encodeURIComponent(formData.procedure_name)}`,
            { credentials: 'include' }
          );
          const searchData = await searchResponse.json();
          const exists = searchData.results?.some(
            item => item.name.toLowerCase() === formData.procedure_name.toLowerCase()
          );
          
          if (!exists) {
            await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/suggest-master-item`, {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
              body: JSON.stringify({
                item_type: 'procedure',
                item_name: formData.procedure_name.trim(),
                item_details: {
                  description: `Auto-suggested from procedure order`
                }
              })
            });
          }
        } catch (err) {
          logger.debug('Auto-suggestion failed for:', formData.procedure_name);
        }
      }
      
      await procedureAPI.create(data);
      toast.success('Procedure scheduled successfully');
      setDialogOpen(false);
      
      // If came from patient detail, navigate back to patient detail page
      if (patientIdFromUrl) {
        navigate(`/patients/${patientIdFromUrl}`);
      } else {
        setFormData({
          patient_id: '',
          procedure_name: '',
          procedure_type: '',
          doctor_id: '',
          scheduled_date: '',
          notes: '',
          price: 0,
        });
        fetchData();
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to schedule procedure'));
    }
  };

  const openUpdateDialog = (procedure) => {
    setSelectedProcedure(procedure);
    setUpdateDialogOpen(true);
  };

  const handleUpdateStatus = async (status) => {
    try {
      await procedureAPI.update(selectedProcedure.id, { status });
      toast.success('Procedure updated');
      setUpdateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update procedure');
    }
  };

  return (
    <div data-testid="procedures-page">
      <header className="page-header glass-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Procedures</h1>
            <p className="text-slate-500">Schedule and manage medical procedures</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open && patientIdFromUrl) {
              navigate(`/patients/${patientIdFromUrl}`);
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-red-500 hover:bg-red-600 rounded-full" data-testid="schedule-procedure-btn">
                <Plus className="w-4 h-4 mr-2" />
                Schedule Procedure
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-[Manrope]">
                  {selectedPatient ? `Schedule Procedure for ${selectedPatient.full_name}` : 'Schedule New Procedure'}
                </DialogTitle>
              </DialogHeader>

              {/* Patient History */}
              {selectedPatient && patientHistory.length > 0 && (
                <Card className="mt-4">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold">Previous Procedures</CardTitle>
                  </CardHeader>
                  <CardContent className="max-h-40 overflow-y-auto">
                    <div className="space-y-2">
                      {patientHistory.slice(0, 5).map((proc) => (
                        <div key={proc.id} className="p-2 bg-slate-50 rounded-md text-xs">
                          <div className="flex justify-between">
                            <span className="font-medium">{proc.procedure_name}</span>
                            <Badge className={proc.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                              {proc.status}
                            </Badge>
                          </div>
                          <p className="text-slate-500">{proc.procedure_type} - {formatDateTime(proc.created_at)}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {!patientIdFromUrl && (
                  <div>
                    <Label>Patient *</Label>
                    <Select value={formData.patient_id} onValueChange={(v) => { setFormData({...formData, patient_id: v}); fetchPatientHistory(v); }}>
                      <SelectTrigger data-testid="select-patient">
                        <SelectValue placeholder="Select patient" />
                      </SelectTrigger>
                      <SelectContent position="popper" sideOffset={5}>
                        {patients.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.full_name} ({p.patient_id})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {patientIdFromUrl && selectedPatient && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800">
                      <strong>Patient:</strong> {selectedPatient.full_name} ({selectedPatient.patient_id})
                    </p>
                  </div>
                )}

                <div>
                  <Label>Procedure Name *</Label>
                  <SearchableSelect
                    value={formData.procedure_name}
                    onValueChange={(v) => setFormData({...formData, procedure_name: v})}
                    onSuggestNew={() => setShowSuggestModal(true)}
                    searchEndpoint={`${process.env.REACT_APP_BACKEND_URL}/api/procedures/search`}
                    placeholder="Search procedure..."
                    searchPlaceholder="Type to search procedures..."
                    emptyMessage="No procedures found"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Procedure Type *</Label>
                    <Select value={formData.procedure_type} onValueChange={(v) => setFormData({...formData, procedure_type: v})}>
                      <SelectTrigger data-testid="select-type">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {PROCEDURE_TYPES.map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Scheduled Date *</Label>
                    <Input
                      type="datetime-local"
                      value={formData.scheduled_date}
                      onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                      required
                      data-testid="input-date"
                    />
                  </div>
                </div>
                <div>
                  <Label>Performing Doctor *</Label>
                  <Select value={formData.doctor_id} onValueChange={(v) => setFormData({...formData, doctor_id: v})}>
                    <SelectTrigger data-testid="select-doctor">
                      <SelectValue placeholder="Select doctor" />
                    </SelectTrigger>
                    <SelectContent>
                      {doctors.map(d => (
                        <SelectItem key={d.id} value={d.id}>Dr. {d.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Estimated Cost (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value) || 0})}
                    data-testid="input-price"
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Pre-operative instructions, special requirements, etc."
                    data-testid="input-notes"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-red-500 hover:bg-red-600" data-testid="submit-procedure">Schedule</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="page-content">
        <Card>
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
                <Stethoscope className="w-5 h-5 text-red-500" />
                Scheduled Procedures
              </CardTitle>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading procedures...</div>
            ) : procedures.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No procedures found.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>Procedure</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Scheduled</TableHead>
                    <TableHead>Cost</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {procedures.map((proc) => (
                    <TableRow key={proc.id} data-testid={`procedure-row-${proc.id}`}>
                      <TableCell className="font-mono text-sm">{proc.procedure_id}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{proc.patient?.full_name}</p>
                          <p className="text-xs text-slate-500">{proc.patient?.patient_id}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{proc.procedure_name}</TableCell>
                      <TableCell>{proc.procedure_type}</TableCell>
                      <TableCell>{formatDateTime(proc.scheduled_date)}</TableCell>
                      <TableCell>{formatCurrency(proc.price)}</TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(proc.status)}>{proc.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {hasRole(['super_admin', 'admin', 'doctor', 'nurse']) && proc.status !== 'completed' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openUpdateDialog(proc)}
                            data-testid={`update-${proc.id}`}
                          >
                            <ClipboardCheck className="w-4 h-4 mr-1" />
                            Update
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Update Status Dialog */}
      <Dialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Update Procedure Status</DialogTitle>
          </DialogHeader>
          {selectedProcedure && (
            <div className="space-y-4 mt-4">
              <div>
                <p className="text-sm text-slate-500">Procedure</p>
                <p className="font-medium">{selectedProcedure.procedure_name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Current Status</p>
                <Badge className={getStatusColor(selectedProcedure.status)}>{selectedProcedure.status}</Badge>
              </div>
              <div className="flex gap-2 pt-4">
                {selectedProcedure.status === 'pending' && (
                  <Button onClick={() => handleUpdateStatus('in_progress')} className="flex-1">
                    Start Procedure
                  </Button>
                )}
                {selectedProcedure.status === 'in_progress' && (
                  <Button onClick={() => handleUpdateStatus('completed')} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                    Mark Completed
                  </Button>
                )}
                <Button variant="outline" onClick={() => handleUpdateStatus('cancelled')} className="text-red-600">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Suggest New Procedure Modal */}
      <SuggestItemModal
        open={showSuggestModal}
        onClose={() => setShowSuggestModal(false)}
        itemType="procedure"
        onSuggestionCreated={() => {
          toast.info('Your procedure suggestion has been submitted for approval');
        }}
      />
    </div>
  );
};

export default ProcedureList;
